"use client";

import { useConsent } from "./consent";

/**
 * Cookie consent banner.
 *
 * Shows only while the choice is undecided, and only after the stored value
 * has been read — otherwise it flashes on every load for people who already
 * answered.
 *
 * Accept and Decline are the same size and weight on purpose. Making refusal
 * harder than acceptance is the pattern regulators single out, and it would
 * undermine the consent it is meant to collect.
 */
export function ConsentBanner() {
  const { consent, ready, accept, decline } = useConsent();

  if (!ready || consent !== null) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Analytics cookies"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-border/60 bg-surface/[0.98]"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6 sm:py-5">
        <p className="text-[13px] leading-snug text-muted-foreground sm:text-sm sm:leading-relaxed">
          We would like to set analytics cookies to understand how the site is used. They are
          not required to browse, and nothing is loaded until you accept.{" "}
          <a href="/privacy" className="font-medium text-primary-deep hover:underline">
            Privacy policy
          </a>
          .
        </p>
        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={decline}
            className="btn flex-1 sm:flex-none"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={accept}
            className="btn btn-dark flex-1 sm:flex-none"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

/** Footer control so a choice can be revisited — required to withdraw consent. */
export function ConsentSettingsLink({ className }: { className?: string }) {
  const { reset } = useConsent();
  return (
    <button type="button" onClick={reset} className={className}>
      Cookie preferences
    </button>
  );
}
